<template>
  <div
    ref="rootRef"
    class="et"
    :class="{ 'et--compact': compact, 'et--fill-width': fillWidth }"
    role="region"
    :aria-label="ariaLabel"
  >
    <header v-if="!compact" class="et__header">
      <div class="et__titles">
        <h2 class="et__title">{{ title }}</h2>
        <p class="et__subtitle">{{ subtitle }}</p>
      </div>
      <div class="et__controls">
        <div
          v-if="!hideTodayControls"
          class="et__btn-group"
          role="group"
          aria-label="Timeline window navigation"
        >
          <button type="button" class="et__btn" @click="shiftWindow('back')">
            ‹
          </button>
          <button type="button" class="et__btn" @click="recenterToday">
            Today
          </button>
          <button type="button" class="et__btn" @click="shiftWindow('forward')">
            ›
          </button>
        </div>
        <div class="et__btn-group" role="group" aria-label="Timeline scale">
          <button
            v-for="option in SCALE_OPTIONS"
            :key="option.value"
            type="button"
            class="et__btn"
            :class="{ 'et__btn--active': scale === option.value }"
            :aria-pressed="String(scale === option.value)"
            @click="setScale(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </header>

    <div v-if="!loading && !compact" class="et__marker-row">
      <div class="et__marker-spacer" :style="labelColumnStyle" />
      <div class="et__marker-track" :style="trackStyle">
        <p
          v-if="todayMarkerLabel"
          class="et__marker-label"
          :style="todayMarkerStyle"
        >
          {{ todayMarkerLabel }}
        </p>
      </div>
    </div>

    <div class="et__lanes">
      <div
        v-if="showTodayLine"
        class="et__today-line"
        :style="{ left: `${todayLineLeftPx}px` }"
        aria-hidden="true"
      />

      <template v-if="customLaneRows.length">
        <EmployeeTimelineLane
          v-for="lane in customLaneRows"
          :key="lane.key"
          :label="lane.label"
          :sublabel="lane.sublabel"
          :fill-width="fillWidth"
          :label-column-style="labelColumnStyle"
          :track-style="trackStyle"
          :status="laneStatus(lane.key)"
          :empty-text="lane.emptyText"
          :loading="loading"
          :rows="lane.rows"
          :selectable="true"
          :selected-id="selectedPayPlanId"
          :hovered-id="hoveredPayPlanId"
          :today-ms="todayMs"
          @bar-select="onBarSelect"
          @bar-hover="onBarHover"
          @retry="emitRetry(lane.key)"
        />
      </template>

      <template v-else>
        <EmployeeTimelineLane
          label="Occupies Positions"
          :fill-width="fillWidth"
          :label-column-style="labelColumnStyle"
          :track-style="trackStyle"
          :status="laneStatus('occupiesPositions')"
          empty-text="No position assignments"
          :loading="loading"
          :rows="occupiesRows"
          :selectable="selectableAssignments"
          :selected-id="null"
          :today-ms="todayMs"
          @bar-select="onAssignmentSelect"
          @retry="emitRetry('occupiesPositions')"
        />

        <EmployeeTimelineLane
          label="Pay range versions"
          :fill-width="fillWidth"
          :label-column-style="labelColumnStyle"
          :track-style="trackStyle"
          :status="laneStatus('payRangeVersions')"
          empty-text="No pay range versions"
          :loading="loading"
          :rows="versionRows"
          :selectable="payRangeVersionsSelectable"
          :selected-id="selectedPayRangeVersionId"
          :today-ms="todayMs"
          @bar-select="onPayRangeVersionSelect"
          @retry="emitRetry('payRangeVersions')"
        />

        <EmployeeTimelineLane
          label="Pay Plans"
          :fill-width="fillWidth"
          :label-column-style="labelColumnStyle"
          :track-style="trackStyle"
          :status="laneStatus('payPlans')"
          empty-text="No pay plans"
          :loading="loading"
          :rows="payPlanRows"
          :selectable="true"
          :selected-id="selectedPayPlanId"
          :hovered-id="hoveredPayPlanId"
          :today-ms="todayMs"
          @bar-select="onBarSelect"
          @bar-hover="onBarHover"
          @retry="emitRetry('payPlans')"
        />
      </template>
    </div>

    <div class="et__axis">
      <div class="et__axis-tier">
        <div class="et__axis-spacer" :style="labelColumnStyle" />
        <div class="et__axis-track" :style="trackStyle">
          <div
            v-for="unit in yearAxisUnits"
            :key="`y-${unit.startMs}`"
            class="et__axis-cell et__axis-cell--year"
            :class="{ 'et__axis-cell--today': unit.isTodayUnit }"
            :style="{ width: `${unit.widthPx}px` }"
          >
            <span class="et__axis-label">{{ unit.label }}</span>
            <span v-if="unit.endLabel" class="et__axis-label et__axis-label--end">{{
              unit.endLabel
            }}</span>
          </div>
        </div>
      </div>
      <div v-if="subAxisUnits.length" class="et__axis-tier">
        <div class="et__axis-spacer" :style="labelColumnStyle" />
        <div class="et__axis-track" :style="trackStyle">
          <div
            v-for="unit in subAxisUnits"
            :key="`s-${unit.startMs}`"
            class="et__axis-cell et__axis-cell--sub"
            :class="{ 'et__axis-cell--today': unit.isTodayUnit }"
            :style="{ width: `${unit.widthPx}px` }"
          >
            <span class="et__axis-label">{{ unit.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <footer v-if="!compact && !loading && visibleLegend.length" class="et__legend">
      <span class="et__legend-heading">Legend</span>
      <div
        v-for="item in visibleLegend"
        :key="item.state"
        class="et__legend-item"
      >
        <span
          class="et__swatch"
          :class="`et__swatch--${item.state}`"
          aria-hidden="true"
        />
        <span class="et__legend-text">{{ item.label }}</span>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import EmployeeTimelineLane from "./EmployeeTimelineLane.vue";
import {
  SCALE_OPTIONS,
  buildAxisUnits,
  defaultWindowStartMs,
  isTodayInWindow,
  normalizeScale,
  todayLineX,
  trackWidthForScale,
  unitContainingToday,
  windowEndMs,
  shiftWindowStart,
  LABEL_COLUMN_PX,
  COMPACT_LABEL_COLUMN_PX,
} from "@components/Timeline/employeeTimelineGeometry";
import {
  formatAxisMonth,
  formatAxisQuarter,
  formatAxisYear,
  toTime,
} from "@components/Timeline/timelineDateUtils";
import {
  collectVisibleStates,
  layoutRecordRows,
} from "./employeeTimelineLayout";
import { PERIOD_BAR_STATE } from "@components/PeriodBar/periodBarUtils";

const LEGEND_ITEMS = [
  { state: PERIOD_BAR_STATE.DRAFT, label: "Proposed" },
  { state: PERIOD_BAR_STATE.CURRENT, label: "Current" },
  { state: PERIOD_BAR_STATE.LAPSED, label: "Lapsed" },
  { state: PERIOD_BAR_STATE.FUTURE, label: "Future" },
];

const props = defineProps({
  assignments: { type: Array, default: () => [] },
  payRangeVersions: { type: Array, default: () => [] },
  payPlans: { type: Array, default: () => [] },
  today: { type: [String, Date], default: () => new Date().toISOString().slice(0, 10) },
  defaultScale: { type: String, default: "" },
  compact: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  laneLoading: { type: Object, default: () => ({}) },
  laneErrors: { type: Object, default: () => ({}) },
  selectedPayPlanId: { type: [String, Number], default: null },
  /** Pay range version bars open a drawer; off keeps them display-only. */
  payRangeVersionsSelectable: { type: Boolean, default: false },
  selectedPayRangeVersionId: { type: [String, Number], default: null },
  /** Pay plan whose row is hovered/focused — mirrors that hover onto its bar. */
  hoveredPayPlanId: { type: [String, Number], default: null },
  title: {
    type: String,
    default: "Employee timeline",
  },
  subtitle: {
    type: String,
    default:
      "Primary position assignments, pay range versions, and pay plans",
  },
  /**
   * Generic lanes `[{ key, label, sublabel, emptyText, records }]`. When non-empty they
   * replace the three employee lanes (used by the Pay Range detail page).
   */
  lanes: { type: Array, default: () => [] },
  /** Per-state legend text overrides, e.g. `{ draft: "Draft" }`. */
  legendLabels: { type: Object, default: () => ({}) },
  /** List every legend entry, not only the states present on the timeline. */
  fullLegend: { type: Boolean, default: false },
  /** Hide the ‹ Today › window navigation; the scale buttons stay. */
  hideTodayControls: { type: Boolean, default: false },
  ariaLabel: { type: String, default: "Employee timeline" },
  /** Optional ISO date aligned to scale unit start (Storybook / tests). */
  initialWindowStart: { type: String, default: "" },
  /** Stretch the timeline track to the container width (Employee Pay Plans page). */
  fillWidth: { type: Boolean, default: false },
  /** Occupies Positions bars are clickable (emit `assignment-select`). */
  selectableAssignments: { type: Boolean, default: false },
});

const emit = defineEmits([
  "bar-select",
  "pay-range-version-select",
  "retry-lane",
  "bar-hover",
  "assignment-select",
]);

const scale = ref(normalizeScale(props.defaultScale || "years"));

function resolveWindowStart() {
  if (props.initialWindowStart) {
    const t = toTime(props.initialWindowStart);
    if (t != null) return t;
  }
  return defaultWindowStartMs(toTime(props.today), scale.value);
}

const windowStartMs = ref(resolveWindowStart());

watch(
  () => props.defaultScale,
  (val) => {
    if (val) scale.value = normalizeScale(val);
  }
);

watch(
  () => [props.today, scale.value, props.initialWindowStart],
  () => {
    windowStartMs.value = resolveWindowStart();
  }
);

const todayMs = computed(() => toTime(props.today) ?? Date.now());

const rootRef = ref(null);
const measuredTrackWidthPx = ref(trackWidthForScale("years", false));
let trackResizeObserver = null;

function measureFillTrackWidth() {
  const el = rootRef.value;
  if (!el || !props.fillWidth) return;

  const styles = window.getComputedStyle(el);
  const horizontalPadding =
    parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
  const labelPx = labelColumnPx.value + 16;
  const available = el.clientWidth - horizontalPadding - labelPx;
  if (available <= 0) {
    measuredTrackWidthPx.value = trackWidthForScale(scale.value, props.compact);
    return;
  }
  measuredTrackWidthPx.value = Math.max(320, Math.floor(available));
}

const trackWidthPx = computed(() => {
  if (props.fillWidth) return measuredTrackWidthPx.value;
  return trackWidthForScale(scale.value, props.compact);
});

const labelColumnPx = computed(() =>
  props.compact ? COMPACT_LABEL_COLUMN_PX : LABEL_COLUMN_PX
);

const labelColumnStyle = computed(() => ({
  width: `${labelColumnPx.value}px`,
  minWidth: `${labelColumnPx.value}px`,
}));

const trackStyle = computed(() => {
  if (props.fillWidth) {
    return {
      flex: "1 1 0",
      width: `${trackWidthPx.value}px`,
      minWidth: 0,
      maxWidth: "100%",
    };
  }
  return {
    width: `${trackWidthPx.value}px`,
    minWidth: `${trackWidthPx.value}px`,
  };
});

const windowEndMsValue = computed(() =>
  windowEndMs(windowStartMs.value, scale.value)
);

function setScale(next) {
  scale.value = normalizeScale(next);
  windowStartMs.value = defaultWindowStartMs(todayMs.value, scale.value);
}

function shiftWindow(direction) {
  windowStartMs.value = shiftWindowStart(
    windowStartMs.value,
    scale.value,
    direction === "forward" ? "forward" : "back"
  );
}

function recenterToday() {
  windowStartMs.value = defaultWindowStartMs(todayMs.value, scale.value);
}

const todayInsideWindow = computed(() =>
  isTodayInWindow(todayMs.value, windowStartMs.value, windowEndMsValue.value)
);

const showTodayLine = computed(() => !props.loading && todayInsideWindow.value);

const todayLineLeftPx = computed(() => {
  const lineX = todayLineX(
    todayMs.value,
    windowStartMs.value,
    windowEndMsValue.value,
    trackWidthPx.value
  );
  return labelColumnPx.value + lineX;
});

const todayMarkerLabel = computed(() => {
  if (todayInsideWindow.value) return "today";
  const lineX = todayLineX(
    todayMs.value,
    windowStartMs.value,
    windowEndMsValue.value,
    trackWidthPx.value
  );
  if (lineX <= 0) return "‹ today";
  if (lineX >= trackWidthPx.value) return "today ›";
  return "today";
});

const todayMarkerStyle = computed(() => {
  const trackLeft = labelColumnPx.value;
  const x = todayLineX(
    todayMs.value,
    windowStartMs.value,
    windowEndMsValue.value,
    trackWidthPx.value
  );
  if (!todayInsideWindow.value && x <= 0) {
    return { left: `${trackLeft}px` };
  }
  if (!todayInsideWindow.value && x >= trackWidthPx.value) {
    return {
      left: `${trackLeft + trackWidthPx.value}px`,
      transform: "translateX(-100%)",
    };
  }
  return { left: `${trackLeft + x}px`, transform: "translateX(-50%)" };
});

const todayUnit = computed(() =>
  unitContainingToday(windowStartMs.value, todayMs.value, scale.value)
);

function buildYearAxisUnits() {
  const z = scale.value;
  if (z === "years") {
    return buildAxisUnits(windowStartMs.value, z).map((unit, index, arr) => ({
      ...unit,
      label: formatAxisYear(unit.startMs),
      endLabel:
        index === arr.length - 1
          ? String(new Date(addYearEnd(unit.startMs)).getUTCFullYear())
          : "",
      isTodayUnit: todayUnit.value?.startMs === unit.startMs,
    }));
  }
  const yearStarts = new Map();
  buildAxisUnits(windowStartMs.value, z).forEach((unit) => {
    const y = formatAxisYear(unit.startMs);
    if (!yearStarts.has(y)) {
      yearStarts.set(y, {
        startMs: unit.startMs,
        widthPx: 0,
        label: y,
        endLabel: "",
        isTodayUnit: false,
      });
    }
    const entry = yearStarts.get(y);
    entry.widthPx += unit.widthPx;
    if (todayUnit.value && unit.startMs === todayUnit.value.startMs) {
      entry.isTodayUnit = true;
    }
  });
  return [...yearStarts.values()];
}

function addYearEnd(ms) {
  return Date.UTC(new Date(ms).getUTCFullYear() + 1, 0, 1);
}

function scaleAxisUnitsToTrack(units, totalPx) {
  if (!units.length) return units;
  const sum = units.reduce((acc, unit) => acc + unit.widthPx, 0);
  if (sum <= 0) {
    const widthPx = totalPx / units.length;
    return units.map((unit) => ({ ...unit, widthPx }));
  }
  return units.map((unit) => ({
    ...unit,
    widthPx: (unit.widthPx / sum) * totalPx,
  }));
}

const yearAxisUnits = computed(() => {
  const units = buildYearAxisUnits();
  if (!props.fillWidth) return units;
  return scaleAxisUnitsToTrack(units, trackWidthPx.value);
});

const subAxisUnits = computed(() => {
  const z = scale.value;
  if (z === "years") return [];
  const units = buildAxisUnits(windowStartMs.value, z).map((unit) => ({
    ...unit,
    label: z === "quarters" ? formatAxisQuarter(unit.startMs) : formatAxisMonth(unit.startMs),
    isTodayUnit: todayUnit.value?.startMs === unit.startMs,
  }));
  if (!props.fillWidth) return units;
  return scaleAxisUnitsToTrack(units, trackWidthPx.value);
});

watch(
  () => [props.fillWidth, scale.value],
  () => {
    if (props.fillWidth) measureFillTrackWidth();
  }
);

onMounted(() => {
  if (!props.fillWidth) return;
  measureFillTrackWidth();
  if (typeof ResizeObserver === "undefined") return;
  trackResizeObserver = new ResizeObserver(() => measureFillTrackWidth());
  if (rootRef.value) trackResizeObserver.observe(rootRef.value);
});

onBeforeUnmount(() => {
  trackResizeObserver?.disconnect();
  trackResizeObserver = null;
});

function layoutLane(records) {
  return layoutRecordRows(
    records,
    windowStartMs.value,
    windowEndMsValue.value,
    trackWidthPx.value,
    todayMs.value
  );
}

const occupiesRows = computed(() => layoutLane(props.assignments));
const versionRows = computed(() => layoutLane(props.payRangeVersions));
const payPlanRows = computed(() => layoutLane(props.payPlans));

function laneStatus(key) {
  if (props.laneErrors?.[key]) return "error";
  if (props.loading || props.laneLoading?.[key]) return "loading";
  return "ready";
}

const customLaneRows = computed(() =>
  (props.lanes || []).map((lane) => ({
    key: lane.key,
    label: lane.label,
    sublabel: lane.sublabel || "",
    emptyText: lane.emptyText || "No records",
    rows: layoutLane(lane.records || []),
  }))
);

const visibleLegend = computed(() => {
  const states = collectVisibleStates([
    props.assignments,
    props.payRangeVersions,
    props.payPlans,
    ...(props.lanes || []).map((lane) => lane.records || []),
  ]);
  return LEGEND_ITEMS.filter((item) => props.fullLegend || states.has(item.state)).map((item) => ({
    ...item,
    label: props.legendLabels?.[item.state] || item.label,
  }));
});

function onBarSelect(id) {
  emit("bar-select", id);
}

function onPayRangeVersionSelect(id) {
  emit("pay-range-version-select", id);
}

function onAssignmentSelect(id) {
  emit("assignment-select", id);
}

/** Single-argument contract: the hovered bar id, or null when the hover ends. */
function onBarHover(id, isHovered) {
  emit("bar-hover", isHovered ? id : null);
}

function emitRetry(laneKey) {
  emit("retry-lane", laneKey);
}
</script>

<style scoped>
.et {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  font-family: Inter, sans-serif;
}

.et--fill-width {
  width: 100%;
}

.et--fill-width .et__marker-row,
.et--fill-width .et__axis-tier {
  display: flex;
  width: 100%;
}

.et--fill-width .et__marker-track,
.et--fill-width .et__axis-track {
  display: flex;
}

.et__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.et__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  color: #101828;
}

.et__subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: #667085;
}

.et__controls {
  display: flex;
  flex-shrink: 0;
  gap: 12px;
  align-items: center;
}

.et__btn-group {
  display: flex;
  overflow: hidden;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.et__btn {
  min-height: 40px;
  padding: 8px 16px;
  border: none;
  border-right: 1px solid #d0d5dd;
  background: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
}

.et__btn:last-child {
  border-right: none;
}

.et__btn--active {
  background: #f9fafb;
}

.et__marker-row {
  display: flex;
  align-items: flex-start;
}

.et__marker-spacer {
  flex-shrink: 0;
}

.et__marker-track {
  position: relative;
  height: 13px;
}

.et__marker-label {
  position: absolute;
  top: 0;
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: #495569;
  white-space: nowrap;
}

.et__marker-label--center {
  transform: translateX(-50%);
}

.et__marker-label--left {
  left: 0;
}

.et__marker-label--right {
  right: 0;
}

.et__lanes {
  position: relative;
  border-top: 1px solid #eaecf0;
}

.et__today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  width: 1px;
  background: #495569;
  pointer-events: none;
}

.et__axis {
  display: flex;
  flex-direction: column;
}

.et__axis-tier {
  display: flex;
}

.et__axis-spacer {
  flex-shrink: 0;
}

.et__axis-track {
  display: flex;
  flex-shrink: 0;
}

.et__axis-cell {
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  height: 20px;
  padding: 6px 4px 0;
  border-top: 1px solid #d0d5dd;
  border-left: 1px solid #d0d5dd;
}

.et__axis-cell--year:last-child {
  border-right: 1px solid #d0d5dd;
}

.et__axis-cell--sub:last-child {
  border-right: 1px solid #d0d5dd;
}

.et__axis-cell--today {
  background: #f9fafb;
}

.et__axis-label {
  overflow: hidden;
  font-size: 11px;
  font-weight: 400;
  color: #667085;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.et__axis-cell--today .et__axis-label {
  font-weight: 500;
  color: #344054;
}

.et__legend {
  display: flex;
  gap: 16px;
  align-items: center;
}

.et__legend-heading {
  width: 184px;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
}

.et__legend-item {
  display: flex;
  gap: 6px;
  align-items: center;
}

.et__legend-text {
  font-size: 12px;
  font-weight: 400;
  color: #667085;
}

.et__swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.et__swatch--draft {
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  border: 1px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
}

.et__swatch--current {
  background: #dcfae6;
  border: 1px solid #75e0a7;
}

.et__swatch--lapsed {
  background: #f2f4f7;
  border: 1px solid #d0d5dd;
}

.et__swatch--future {
  background: #eff8ff;
  border: 1px solid #b2ddff;
}

.et--compact {
  gap: 0;
  padding: 12px;
}

.et--compact .et__axis-spacer,
.et--compact .et__marker-spacer {
  width: 140px !important;
  min-width: 140px !important;
}
</style>
